/**
 * Modal Carousel Component
 * @module patterns/components/carousels/modal-carousel
 */
/* tslint:disable:no-any */

// Dependencies
import Glide from '@glidejs/glide';
import './modal-carousel.scss';

// Local Dependencies
import { markElementReady } from '@root/src/framework/scripts/helpers/element-ready';
import { parseToObject } from '@root/src/framework/scripts/utils/parse';
import { CLASS_NAME, OPTIONS } from './constants';

/**
 * Initialises the Carousel component
 */

const bindCarouselIcons = ( item: Element, iconName: String, dataType: String) => {
    const icon = item.querySelector(`.${iconName}`) as HTMLElement;
    // Getting first instance of the relevant type 
    const iconData = item.querySelector(`.carousel__slide:not(.glide__slide--clone) [data-type="${dataType}"]`) as HTMLElement;

    if (icon && iconData){
        const idx = Number(iconData.dataset.index);
        // Assigning first instance index to the icon
        icon.dataset.glideDir = `=${idx}`;
        icon.classList.remove('hidden');
    } else {
        // Hiding any icons which aren't in the list
        icon.classList.add('hidden');
    }
}

export const carousels: Map<string, Glide> = new Map();

export function carousel() {
    const listeners: [Element, string, EventListenerOrEventListenerObject][] = [];
    const elements = document.querySelectorAll(`.${CLASS_NAME}`);
	
    // Loop through and individually instantiate each Glide instance
    elements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        const carouselOptions = parseToObject<any>(htmlElement.dataset.carouselOptions);
        const carouselId = htmlElement.dataset.carouselId;
        const thumbnails = element.querySelectorAll(`.thumbnails`) as NodeListOf<HTMLElement>;
        const videoSlides: NodeListOf<HTMLElement> = element.querySelectorAll('.video-slide');
        const slideArrows = element.querySelectorAll('.icon-button')
        const additionalInformation = element.querySelectorAll('.additional-information');


        // media selctor icons (floorplan, video, image)
        const mediaSelectorIcons = element.querySelectorAll(".icon-list svg");

        const indexClass = `${CLASS_NAME}-${carouselId}`;
        htmlElement.classList.add(indexClass);

        // Resets and Updates Media Selectors Icons to remove/add selectored colour
        const UpdateMediaSelectorIcons = () => {
            if (mediaSelectorIcons){
                mediaSelectorIcons.forEach(icon => {
                    if (icon && icon.parentElement && Number(icon.parentElement.dataset.glideDir?.replace("=","")) != glide.index){
                        icon.classList.remove('!fill-white', '!stroke-white');
                    } else {
                        if (icon.classList.contains('icon-fill')){
                            icon.classList.add('!fill-white')
                        } else if (icon.classList.contains('icon-stroke')) {
                            icon.classList.add('!stroke-white')
                        }
                    }
                })
            }
        }

        // Update thumbnail view to show selected thumbnail + 4
        const updateThumbnails = () => {
            const additionalThumbnailToShow = 4;
            const startAtClick = Number(glide.index);
            const thumbnailLength = thumbnails.length - 1;
            const thumbnailStart = startAtClick > thumbnailLength - additionalThumbnailToShow ? thumbnailLength - additionalThumbnailToShow : startAtClick;
            const thumbnailEnd = startAtClick + additionalThumbnailToShow;

            thumbnails.forEach(thumbnail =>{
                thumbnail.classList.remove('md:flex');
				
                const dataIdx = Number(thumbnail.dataset.glideDir?.replace("=",""));
                if (dataIdx >= thumbnailStart && dataIdx <= thumbnailEnd){
                    thumbnail.classList.add('md:flex')
                }
            });
        }

        // Updates the number of values on mobile
        const updateMobileIndex = () => {
            const mobileIndex = element.querySelector(`.image-index`) as HTMLElement;
			
            if (mobileIndex){
                mobileIndex.innerHTML = `${Number(glide.index)+1} of ${thumbnails.length}`
            }
        }

        // Reset the video media items to view thumbnails and play buttons and assign click event to toggle video 
        const handleVideoMedia = () => {
            if (videoSlides){
                videoSlides.forEach( videoSlide => {
                    const videoThumbnail = videoSlide.querySelector('img');
                    const videoPlayButton = videoSlide.querySelector('.play-button');
                    const video = videoSlide.querySelector('iframe');

                    if (videoThumbnail && videoPlayButton && video){
                        const videoLink = video.dataset.videoLink;
                        if (videoLink){

                            // Reset video links
                            videoPlayButton.classList.remove('hidden');
                            videoThumbnail.classList.remove('hidden');
                            additionalInformation.forEach(info =>{
                                info.classList.remove('hidden')
                            })
                            video.classList.add('hidden');
                            video.removeAttribute("src");

                            // Play video and hide play icon and thumbnail image
                            const playVideo = () => {
                                videoPlayButton.classList.add('hidden');
                                videoThumbnail.classList.add('hidden');
                                additionalInformation.forEach(info =>{
                                    info.classList.add('hidden')
                                })
                                video.classList.remove('hidden');
                                video.setAttribute("src", videoLink + "?autoplay=1&amp;muted=1");
                            }

                            // Assign event listerns to all the play buttons
                            videoPlayButton.addEventListener("click", playVideo);
                            listeners.push([videoPlayButton, "click", playVideo]);
                        }
                    }
                })
            }
        }
		

        bindCarouselIcons(element, 'icon-floorplan', 'floorplan');
        bindCarouselIcons(element, 'icon-camera', 'image');
        bindCarouselIcons(element, 'icon-play', 'video');
		
        // Add click event to the slide arrows to reset the video media items
        if (slideArrows){
            slideArrows.forEach(slideArrow => {
                slideArrow.addEventListener("click", handleVideoMedia);
                listeners.push([slideArrow, "click", handleVideoMedia]);
            })
        }
	
        const glide = new Glide(`.${indexClass}`, {
            ...OPTIONS,
            ...carouselOptions,
        });

        glide.on('update', function() {
            updateMobileIndex();
            UpdateMediaSelectorIcons();
            updateThumbnails();
            handleVideoMedia();
        });

        glide.on('swipe.end', function(){
            updateMobileIndex()
        })

        glide.on('move.after', function(){
            updateThumbnails();
            updateMobileIndex();
            UpdateMediaSelectorIcons();
        })

        glide.mount();
        carousels.set(indexClass, glide);

        markElementReady(htmlElement);

    });

    const destroyModalCarousel = () => {
        // Destory each Glide instance to prevent memory leaks
        for (let [, glide] of carousels) {
            glide.destroy();
        }

        for (const [element, event, listener] of listeners) {
            element.removeEventListener(event, listener);
        }
    };

    return { elements, carousels, destroyModalCarousel };
}
